import React, { useCallback } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Stack } from '@mui/material';

import { ConflictResolution, ExtractedDateRange, RateConflictInfo } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';

import EditableDateRow from './EditableDateRow';
import StopsellRateRow from './StopsellRateRow';

interface Props {
  date: ExtractedDateRange;
  dateIndex: number;
  catalogRoomRates: Array<{ id: string; name: string }>;
  rateConflicts: Array<RateConflictInfo>;
  rateResolutions: Record<string, ConflictResolution>;
  onToggle: (dateIndex: number, value: boolean) => void;
  onEdit: (dateId: string, newRange: { start: string; end: string }) => void;
  onDelete: (dateId: string) => void;
  onResolve: (dateId: string, rateId: string, status: ConflictResolution) => void;
  onUndo: (dateId: string, rateId: string) => void;
}

export default function StopsellExtractedDateRow({
  date,
  dateIndex,
  catalogRoomRates,
  rateConflicts,
  rateResolutions,
  onToggle,
  onEdit,
  onDelete,
  onResolve,
  onUndo,
}: Props) {
  const handleToggle = useCallback((v: boolean) => onToggle(dateIndex, v), [onToggle, dateIndex]);
  const handleEdit = useCallback((range: { start: string; end: string }) => onEdit(date.id, range), [onEdit, date.id]);
  const handleDelete = useCallback(() => onDelete(date.id), [onDelete, date.id]);

  return (
    <EditableDateRow
      start={date.start}
      end={date.end}
      selected={date.selected}
      onToggle={handleToggle}
      onEdit={handleEdit}
      onDelete={handleDelete}
      icon={<EventBusyOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
      chipSx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: 11 }}
      editLabel={`Edit ${formatDateRange(date.start, date.end)}`}
      deleteLabel={`Delete ${formatDateRange(date.start, date.end)}`}
    >
      {catalogRoomRates.length > 0 && date.selected && (
        <Stack gap={0.5} sx={{ pl: 4 }}>
          {catalogRoomRates.map((rate, rateIndex) => {
            const conflict = rateConflicts[rateIndex] ?? { status: 'clear' as const };
            const resolution = rateResolutions[`${date.id}-${rate.id}`] ?? null;
            return (
              <StopsellRateRow
                key={rate.id}
                rateName={rate.name}
                rateConflict={conflict}
                resolution={resolution}
                onResolve={(status) => onResolve(date.id, rate.id, status)}
                onUndo={() => onUndo(date.id, rate.id)}
              />
            );
          })}
        </Stack>
      )}
    </EditableDateRow>
  );
}
